import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle2, Calendar, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConsultationModal = ({ isOpen, onClose, source = 'construction_page', projectTypes }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/consultation/book`, { ...formData, source });

      if (response.data.success) {
        setSubmitted(true);
        
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            projectType: '',
            budget: '',
            timeline: '',
            message: ''
          });
          onClose();
        }, 3000);
      }
    } catch (err) {
      console.error('Consultation booking error:', err);
      setError('Failed to book consultation. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[rgb(0,55,32)]">
                Book Your Free Consultation
              </DialogTitle>
              <DialogDescription className="text-base text-[rgb(131,146,140)] mt-2">
                Let's discuss your renovation project. We'll contact you within 24 hours to schedule your in-home consultation.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cons-name" className="text-[rgb(0,55,32)]">Full Name *</Label>
                  <Input
                    id="cons-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="John Smith"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cons-phone" className="text-[rgb(0,55,32)]">Phone Number *</Label>
                  <Input
                    id="cons-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="613-XXX-XXXX"
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="cons-email" className="text-[rgb(0,55,32)]">Email Address *</Label>
                <Input
                  id="cons-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="cons-project" className="text-[rgb(0,55,32)]">Project Type *</Label>
                <Select value={formData.projectType} onValueChange={(value) => handleChange('projectType', value)} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    {(projectTypes || [
                      { value: "full-renovation", label: "Full Home Renovation" },
                      { value: "sdu", label: "SDU Creation" },
                      { value: "kitchen", label: "Kitchen Remodeling" },
                      { value: "bathroom", label: "Bathroom Renovation" },
                      { value: "basement", label: "Basement Finishing" },
                      { value: "other", label: "Other" }
                    ]).map(pt => (
                      <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cons-budget" className="text-[rgb(0,55,32)]">Budget Range</Label>
                  <Select value={formData.budget} onValueChange={(value) => handleChange('budget', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-25k">Under $25,000</SelectItem>
                      <SelectItem value="25k-50k">$25,000 - $50,000</SelectItem>
                      <SelectItem value="50k-100k">$50,000 - $100,000</SelectItem>
                      <SelectItem value="100k-plus">$100,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cons-timeline" className="text-[rgb(0,55,32)]">Desired Timeline</Label>
                  <Select value={formData.timeline} onValueChange={(value) => handleChange('timeline', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asap">ASAP</SelectItem>
                      <SelectItem value="1-3-months">1-3 Months</SelectItem>
                      <SelectItem value="3-6-months">3-6 Months</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="cons-message" className="text-[rgb(0,55,32)]">Project Details</Label>
                <Textarea
                  id="cons-message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Tell us about your project..."
                  rows={4}
                  disabled={loading}
                  className="mt-1"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold h-12 rounded-full hover:scale-102 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Free Consultation
                  </>
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="py-8 text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-[var(--accent-primary)]" />
            </div>
            <h3 className="text-2xl font-bold text-[rgb(0,55,32)] mb-2">Consultation Booked!</h3>
            <p className="text-[rgb(131,146,140)]">
              We'll contact you within 24 hours to confirm your appointment.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationModal;
